/**
 * V70.28 — 인력 현황 Dashboard 전용 고용형태 semantic palette (단일 정의 지점)
 *
 * 원칙
 *  - KPI / stacked bar / donut / legend / 표에서 **동일 고용형태는 항상 동일 색상**
 *  - muted business tone (강한 원색 금지) — 기존 nGle-Works UI 와 어울리도록
 *  - 색상만으로 구분하지 않는다. 모든 표현에 **label 을 반드시 함께** 제공한다.
 *  - 컴포넌트마다 색을 재정의하지 않고 이 파일만 import 한다.
 *
 * 고용형태 코드는 BE enum 4개로 고정 (REGULAR/CONTRACT/INTERN/DISPATCH).
 * 표시 순서도 여기서 결정한다 (정규직 → 파견직 → 계약직 → 인턴: 인원 많은 축부터).
 */

export const EMPLOYMENT_TYPE_ORDER = ['REGULAR', 'DISPATCH', 'CONTRACT', 'INTERN'] as const;
export type EmploymentTypeKey = typeof EMPLOYMENT_TYPE_ORDER[number];

export interface EmploymentTypeStyle {
  /** 차트 채움 색 */
  color: string;
  /** 연한 배경 (KPI 카드 등) */
  soft: string;
  /** 한글 풀네임 */
  label: string;
}

export const EMPLOYMENT_TYPE_STYLE: Record<EmploymentTypeKey, EmploymentTypeStyle> = {
  // 짙은 슬레이트 블루 — 기간 제한 없는 핵심 인력
  REGULAR: { color: '#3f5f8a', soft: '#eef2f8', label: '정규직' },
  // 차분한 틸 — 가장 인원이 많을 수 있어 과하지 않은 톤
  DISPATCH: { color: '#4f8a86', soft: '#eef5f4', label: '파견직' },
  // 머스터드 브라운 — 계약 만료 관리 대상
  CONTRACT: { color: '#b08440', soft: '#faf4ea', label: '계약직' },
  // 뮤트 퍼플 — 소수 인원이어도 식별 가능
  INTERN: { color: '#8a6a9c', soft: '#f5f1f8', label: '인턴' },
};

/** 입사/퇴사 bar 색 (고용형태 색과 혼동되지 않는 별도 축) */
export const JOIN_COLOR = '#2f7d5c';
export const LEAVE_COLOR = '#a8544c';

export const CHART_GRID = '#e5e7eb';
export const CHART_AXIS_TEXT = '#6b7280';

export function typeStyle(code: string): EmploymentTypeStyle {
  return EMPLOYMENT_TYPE_STYLE[code as EmploymentTypeKey]
    ?? { color: '#9ca3af', soft: '#f3f4f6', label: code || '-' };
}

/**
 * V70.37: 계약 만료 **확정 결과** 3상태 색상
 *   고용형태 palette 와 혼동되지 않도록 별도 축으로 정의한다.
 *   (의미 축: 종료=떠남 / 전환=형태 변경 / 연장=유지)
 *
 *   V70.35~36 의 4번째 상태('미결')는 제거했다. 실행되지도 예약되지도 않은
 *   만료 건을 하나의 '상태'로 보여주면 결과를 추측한 것처럼 읽히기 때문이다.
 *   해당 건은 `만료 도래(total)` 에는 그대로 남고, 확정 비율 분모에서만 빠진다.
 */
export const CONTRACT_END_ORDER = ['ended', 'converted', 'extended'] as const;
export type ContractEndKey = typeof CONTRACT_END_ORDER[number];

export const CONTRACT_END_STYLE: Record<ContractEndKey, { color: string; label: string }> = {
  ended: { color: '#a8544c', label: '종료' },       // 퇴사 — 월별 입·퇴사의 '퇴사'와 동일 색
  converted: { color: '#3f5f8a', label: '전환' },   // 정규직 색과 동일 계열 (고용형태 변경)
  extended: { color: '#4f8a86', label: '연장' },    // 유지/재계약
};

/** 만료 도래 막대의 '확정 결과 없음' 잔여 영역 — 상태 라벨 없이 바탕으로만 표현 */
export const CONTRACT_END_GROUND = '#eef0f4';

export function contractEndStyle(key: string): { color: string; label: string } {
  return CONTRACT_END_STYLE[key as ContractEndKey] ?? { color: '#9ca3af', label: key };
}
